/**
 * Edit Applicator - Applies edit-mode changes to HTML
 * Applies transforms and styles to elements
 */

/**
 * Convert camelCase to kebab-case for CSS properties
 * Example: backgroundColor -> background-color
 */
function camelToKebab(str: string): string {
  return str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
}

interface EditData {
  transform?: string;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
  position?: {
    left: number;
    top: number;
  };
  textContent?: string;
  textStyles?: Record<string, string | number>;
  backgroundStyles?: Record<string, string>;
  borderStyles?: Record<string, string | number>;
  effectsStyles?: Record<string, string | number>;
  spacingStyles?: Record<string, string | number>;
  additionalStyles?: Record<string, string>;
}

/**
 * Apply edits to preprocessed HTML
 * Applies CSS transforms and other styles to preserve layout
 * @param preprocessedHtml - HTML with data-edit-id attributes
 * @param edits - Object mapping edit IDs to edit data
 * @returns Modified HTML with edits applied as inline styles
 */
export function applyEditsToHtml(
  preprocessedHtml: string,
  edits: Record<string, EditData>
): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(preprocessedHtml, 'text/html');
    
    console.log('🎨 [APPLY-TO-HTML] Starting to apply edits to HTML:', {
      editCount: Object.keys(edits).length,
      editIds: Object.keys(edits),
      timestamp: new Date().toISOString()
    });
    
    // Get all edit IDs in the preprocessed HTML
    const allEditIds = Array.from(doc.querySelectorAll('[data-edit-id]')).map(el => el.getAttribute('data-edit-id'));
    console.log('📋 [APPLY-TO-HTML] Edit IDs in preprocessedHtml:', {
      count: allEditIds.length,
      firstFew: allEditIds.slice(0, 5),
      timestamp: new Date().toISOString()
    });
    
    let appliedCount = 0;
    let notFoundCount = 0;
    
    // Apply each edit
    Object.entries(edits).forEach(([editId, editData]) => {
      const element = doc.querySelector(`[data-edit-id="${editId}"]`);
      if (!element) {
        notFoundCount++;
        console.warn(`❌ [APPLY-TO-HTML] Element with edit-id ${editId} not found in preprocessedHtml`);
        return;
      }
      appliedCount++;
      
      // Apply text content
      if (editData.textContent !== undefined) {
        element.textContent = editData.textContent;
      }
      
      // Apply transform directly to preserve layout
      if (editData.transform) {
        const htmlElement = element as HTMLElement;
        htmlElement.style.transform = editData.transform;
      }
      
      // Apply text styles
      if (editData.textStyles) {
        const htmlElement = element as HTMLElement;
        Object.entries(editData.textStyles).forEach(([property, value]) => {
          if (property === 'fontSize') {
            htmlElement.style.fontSize = `${value}px`;
          } else {
            htmlElement.style.setProperty(camelToKebab(property), String(value));
          }
        });
      }
      
      // Apply background styles
      if (editData.backgroundStyles) {
        const htmlElement = element as HTMLElement;
        Object.entries(editData.backgroundStyles).forEach(([property, value]) => {
          if (property === 'backgroundImage') {
            // Wrap URL in url() if needed
            if (value && value !== 'none' && !value.startsWith('url(')) {
              htmlElement.style.backgroundImage = `url(${value})`;
            } else {
              htmlElement.style.backgroundImage = value;
            }
          } else {
            htmlElement.style.setProperty(camelToKebab(property), value);
          }
        });
      }
      
      // Apply border styles
      if (editData.borderStyles) {
        const htmlElement = element as HTMLElement;
        Object.entries(editData.borderStyles).forEach(([property, value]) => {
          if (property === 'borderWidth' || property === 'borderRadius') {
            htmlElement.style.setProperty(camelToKebab(property), `${value}px`);
          } else {
            htmlElement.style.setProperty(camelToKebab(property), String(value));
          }
        });
      }
      
      // Apply effects styles
      if (editData.effectsStyles) {
        const htmlElement = element as HTMLElement;
        Object.entries(editData.effectsStyles).forEach(([property, value]) => {
          htmlElement.style.setProperty(camelToKebab(property), String(value));
        });
      }
      
      // Apply spacing styles
      if (editData.spacingStyles) {
        const htmlElement = element as HTMLElement;
        Object.entries(editData.spacingStyles).forEach(([property, value]) => {
          // All spacing properties need px units
          htmlElement.style.setProperty(camelToKebab(property), `${value}px`);
        });
      }
      
      // Apply any additional styles
      if (editData.additionalStyles) {
        const htmlElement = element as HTMLElement;
        Object.entries(editData.additionalStyles).forEach(([property, value]) => {
          htmlElement.style.setProperty(camelToKebab(property), value);
        });
      }
    });
    
    console.log(`✅ [APPLY-TO-HTML] Summary:`, {
      total: Object.keys(edits).length,
      applied: appliedCount,
      notFound: notFoundCount,
      timestamp: new Date().toISOString()
    });
    
    return doc.documentElement.outerHTML;
  } catch (error) {
    console.error('Error applying edits to HTML:', error);
    return preprocessedHtml; // Return original if application fails
  }
}

/**
 * Get edits from iframe localStorage
 * @param iframeWindow - Reference to iframe's window object
 * @returns Edits object from localStorage
 */
export function getEditsFromIframe(iframeWindow: Window): Record<string, EditData> {
  try {
    const stored = iframeWindow.localStorage.getItem('beemi-edit-mode');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to get edits from iframe:', error);
    return {};
  }
}

/**
 * Clear edits from iframe localStorage
 * @param iframeWindow - Reference to iframe's window object
 */
export function clearEditsFromIframe(iframeWindow: Window): void {
  try {
    iframeWindow.localStorage.removeItem('beemi-edit-mode');
    console.log('Cleared edits from iframe localStorage');
  } catch (error) {
    console.error('Failed to clear edits from iframe:', error);
  }
}

